import React, { useCallback, useEffect, useState } from 'react';
import CommandPanelImageComponent from 'components/commandpanel/CommandPanelImageComponent';
import './focuslevel.scss';
import { useRockFetchPost } from 'utils/RockFetch';
import { useRMDispatch } from 'redux/hooks';
import { setDropImageForKey } from 'redux/reducers/experimentReducer';
import { CustomCheckBox } from 'components/input';
import { ITabItem } from 'components/tab';
import { BaseSidePanel } from 'components/sidePanel';
import useFrapDataPointsExport from 'CustomHooks/useFrapDataPointsExport';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
  useWellDropImportFlToggle,
  useAllRegions,
} from 'redux/hooks/experiment';
import ImagingToggles, {
  ImagingToggleProps,
} from '../ExperimentPanel/ExperimentTools/ImagingTools';
import SideBarComponent from '../ExperimentPanel/SideBarComponent/SideBarComponent';
import { useFLDropImageByKey, useShowFrapGraph } from 'redux/hooks/well';
import useRMLite from 'CustomHooks/useRMLite';
import {
  useUpdateBFLinFocusLevelImageStore,
  useFocusLevelImages,
} from 'components/well/hooks';
import { WellDetailsStates } from 'business/models/CustomModels';
import { getWellDropImageUniqueKeyByWellDetails } from 'redux/utils/well';
import { updateWellDropImageBFLDetails } from 'redux/reducers/wellReducer';
import { getRegionIdCached } from 'components/well/utils';
import { ERegions } from 'enums/ERegions';
import { EImageTypes } from 'enums/EImageType';

function FocusLevel() {
  const dispatch = useRMDispatch();
  const [rockFetchPost] = useRockFetchPost();

  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const regions = useAllRegions();
  const { wellNum, plateID, plateNum } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = selectedDropImageParameters || {};
  const regionID = getRegionIdCached(plateID, wellNum, dropNumber, regionName, regions);

  const wellDetails = {
    wellNum,
    plateNum,
    plateID,
    cpID: cpId,
    dropNumber,
    inspectionSet: inspection,
    regionID,
    imageBatchId: inspection?.groupedInspections?.find((gi) => gi.plateId === plateID)
      ?.imageBatchId,
  } as WellDetailsStates;
  const UIKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);
  const flRedux = useFLDropImageByKey(UIKey);
  const [focusLevelsData, setFocusLevelsData] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [bestFocusImageId, setBestFocusImageId] = useState(-1);
  const [extendedFocusImageId, setExtendedFocusImageId] = useState(-1);
  const [extendedFocusImageTitle, setExtendedFocusImageTitle] = useState(
    flRedux?.imageTitle,
  );
  const [, isFrap] = useFrapDataPointsExport();
  const [importFl, setImportFl] = useWellDropImportFlToggle();
  const [showFrapGraph, setShowFrapGraph] = useShowFrapGraph();
  const rmLite = useRMLite();
  const focusLevelImages = useFocusLevelImages(
    wellNavigationDetails,
    selectedDropImageParameters,
  );
  const updateBFLinFocusLevelImageStore = useUpdateBFLinFocusLevelImageStore();

  useEffect(() => {
    async function setFLInfo() {
      if (!focusLevelImages?.length) {
        return null;
      }
      {
        let extendedFocusImage = 0;
        let extendedFocusImageTitle = '';
        let bestFocusImageId = -1;
        let bestFocusImageTitle = '';

        focusLevelImages.map((fLVM) => {
          if (
            fLVM.imageTypeId == EImageTypes.DropLocation &&
            regionName == ERegions.Overview
          ) {
            bestFocusImageId = fLVM.imageId;
            bestFocusImageTitle = fLVM.imageTitle;
          }
          if (fLVM.imageTypeId == EImageTypes.ExtendedFocus) {
            extendedFocusImage = fLVM.imageId;
            extendedFocusImageTitle = fLVM.imageTitle;
          }

          if (fLVM.isBestFocusLevel) {
            bestFocusImageId = fLVM.imageId;
            bestFocusImageTitle = fLVM.imageTitle;
          }
          if (flRedux?.imageTypeIndex == fLVM.imageTypeIndex) {
            setActiveImage(fLVM.imageTitle);
            dispatch(
              setDropImageForKey({
                key: UIKey,
                image: fLVM as any,
              }),
            );
          }
        });

        setFocusLevelsData(focusLevelImages);
        setExtendedFocusImageId(extendedFocusImage);
        setExtendedFocusImageTitle(extendedFocusImageTitle);
        setBestFocusImageId(bestFocusImageId < 1 ? extendedFocusImage : bestFocusImageId);

        const storedFocusLevel = flRedux?.imageId
          ? focusLevelImages.find((x) => x.imageId === flRedux.imageId)
          : null;
        if (!storedFocusLevel) {
          let focusLevelToUse = null;
          let activeImageTitle = '';

          if (bestFocusImageId >= 1) {
            focusLevelToUse = focusLevelImages.find(
              (x) => x.imageId === bestFocusImageId,
            );
            activeImageTitle = bestFocusImageTitle;
          } else if (extendedFocusImage > 0) {
            focusLevelToUse = focusLevelImages.find(
              (x) => x.imageId === extendedFocusImage,
            );
            activeImageTitle = extendedFocusImageTitle;
          }
          if (focusLevelToUse) {
            dispatch(
              setDropImageForKey({
                key: UIKey,
                image: focusLevelToUse as any,
              }),
            );
            setActiveImage(activeImageTitle);
          } else {
            setActiveImage(flRedux?.imageTitle || extendedFocusImageTitle);
          }
        } else {
          setActiveImage(storedFocusLevel.imageTitle);
        }
      }
    }
    setFLInfo();
  }, [focusLevelImages, UIKey, dispatch]);

  useEffect(() => {
    if (flRedux?.imageId) {
      setActiveImage(flRedux.imageTitle);
    }
  }, [flRedux]);

  function handleOnChangeFocusLevel(flVM) {
    dispatch(
      setDropImageForKey({
        key: UIKey,
        image: { ...flVM, image: flVM.image },
      }),
    );
    setActiveImage(flVM.imageTitle);
    if (showFrapGraph) {
      setShowFrapGraph(false);
    }
  }

  const bestFocusLevelImageSetter = useCallback(
    (flvm) => () => {
      setBestFocusLevelImage(flvm);
      handleOnChangeFocusLevel(flvm);
      updateBFLinFocusLevelImageStore(
        wellNavigationDetails,
        selectedDropImageParameters,
        flvm.imageId,
      );
      dispatch(updateWellDropImageBFLDetails({ key: UIKey, imageDetails: flvm }));
    },
    [
      setBestFocusLevelImage,
      handleOnChangeFocusLevel,
      updateBFLinFocusLevelImageStore,
      dispatch,
      UIKey,
      wellNavigationDetails,
      selectedDropImageParameters,
    ],
  );

  async function setBestFocusLevelImage(flVM) {
    let body;
    if (flVM.imageTypeId !== 4) {
      body = {
        previousBestSelectedImageId: bestFocusImageId,
        selectedImageId: bestFocusImageId,
      };
    } else {
      body = {
        previousBestSelectedImageId: bestFocusImageId,
        selectedImageId: flVM.imageId,
      };
    }

    const response = await rockFetchPost('api/imaging/setBestFocusLevelImage', body);

    if (response == true) {
      if (bestFocusImageId == flVM.imageId) {
        setActiveImage(extendedFocusImageTitle);
        setBestFocusImageId(extendedFocusImageId);
        const extendedFocus = focusLevelsData.find(
          (item) => item.imageId === extendedFocusImageId,
        );
        dispatch(
          setDropImageForKey({
            key: UIKey,
            image: extendedFocus,
          }),
        );
      } else {
        setActiveImage(flVM.imageTitle);
        setBestFocusImageId(flVM.imageId);
      }
    }
  }
  function getFocusLevelsView() {
    return !focusLevelsData
      ? null
      : focusLevelsData.slice(0, isFrap ? -1 : undefined).map((flVM, i) => (
          <CommandPanelImageComponent
            image={flVM.image}
            imageName={flVM.imageTitle}
            key={flVM.imageTitle}
            inspectionName=""
            inspectionDateTime=""
            active={activeImage === flVM.imageTitle}
            onClick={() => handleOnChangeFocusLevel(flVM)}
          >
            {!rmLite && (
              <div
                id="commandpanel-focuslevel-view"
                style={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CustomCheckBox
                  id="commandpanel-focuslevel-view-checkbox"
                  checked={flVM.imageId == bestFocusImageId}
                  onChange={bestFocusLevelImageSetter(flVM)}
                  disabled={
                    flVM.imageTypeId == EImageTypes.ExtendedFocus &&
                    flVM.imageId == bestFocusImageId
                  }
                />
                <div
                  id="commandpanel-focuslevel-view-message"
                  style={{ fontSize: '70%', marginLeft: '7%' }}
                >
                  Select as Best Focus Level
                </div>
              </div>
            )}
          </CommandPanelImageComponent>
        ));
  }

  const getImportFlToggle = () => {
    const importFlToggle: ImagingToggleProps[] = [
      {
        label: 'Enable Import Focus Level',
        isOn: !!importFl,
        onToggle: setImportFl,
        disabled: rmLite,
      },
    ];
    return <ImagingToggles toggles={importFlToggle} />;
  };

  const tabItems_: ITabItem[] = [
    {
      content: (
        <div id="commandpanel-focuslevel-tab-wrapper" className="flex flex-col">
          {!rmLite && (
            <div
              id="commandpanel-focuslevel-importfltoggle-wrapper"
              className="mx-4 my-2"
            >
              {getImportFlToggle()}
            </div>
          )}
          <SideBarComponent
            heading={'Focus Level Thumbnails'}
            isCollapsible={true}
            handleColapse={() => {}}
            ContainerComponent={
              <div
                id="commandpanel-focuslevel-thumbnails-container"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '2%',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  maxHeight: '70vh',
                  overflow: 'auto',
                }}
              >
                {getFocusLevelsView()}
              </div>
            }
          />
        </div>
      ),

      title: 'Focus Level',
    },
  ];

  return <BaseSidePanel tabItems={tabItems_} headerTitle={'Focus Level'} />;
}

export default FocusLevel;
